import { useMemo } from "react";
import { Route, Routes, useParams, useNavigate, useLocation } from "react-router";
import { PrismMenu } from "@prism-ui/react";
import ApplicationPermissions from "./panes/ApplicationPermissions";
import ApplicationRater from "./panes/ApplicationRater";
import ApplicationWindow from "./panes/ApplicationWindow";
import PageHeader from "../PageHeader";
import ManageUsers from "./panes/ManageUsers/ManageUsers.jsx";
import EmailReminders from "./panes/EmailReminders/EmailReminders";
import ProjectTags from "./panes/Tags/index.ts";
import PermissionLevel from "../../data/PermissionLevel";
import Unauthorized from "../Unauthorized";
import "./AdminSettings.css";
import { DataRetention } from "./panes/DataRetention";
import OfficerRoles from "./panes/OfficerRoles/OfficerRoles";
import OfficerManagement from "./panes/OfficerManagement";
import CohortSize from "./panes/CohortSize/index.ts";
import AuditLogs from "./panes/AuditLogs/index.ts";
import { SiteSettings } from "./panes/SiteSettings/index.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders } from "@fortawesome/free-solid-svg-icons";
import RoleTenure from "./panes/RoleTenure/index.ts";
import GraduationPledges from "./panes/GraduationPledges/index.ts";
import HomeContent from "./panes/HomeContent/index.ts";
import ManagerApproval from "./panes/ManagerApproval";
import ProjectFairs from "./panes/ProjectFairs";

type Props = {
  currentUser: { Role?: { permission_level: number } };
  loginAsUser: (id: number) => void;
  refreshCurrentUser?: () => Promise<void>;
};

// Mounted by App.jsx at "/admin-settings/*"; the menu needs absolute hrefs for Prism.
const path = "/admin-settings";

const AdminDashboard = ({ currentUser, loginAsUser, refreshCurrentUser }: Props) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // currentUser can briefly lack Role when the session expires mid-render
  const permissionLevel = currentUser?.Role?.permission_level ?? 0;

  // Routes are sorted alphabetically
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const subRoutes: { [route: string]: [string, any] } = {
    "/application-permissions": ["🔐 Application Permissions", ApplicationPermissions],
    ...permissionLevel >= PermissionLevel.Officer
      ? { "/application-rater": ["⚖️ Application Rater", ApplicationRater], }
      : {},
    "/application-windows": ["🪟 Application Windows", ApplicationWindow],
    ...permissionLevel >= PermissionLevel.Officer
      ? { "/audit-logs": ["🔍 Audit Logs", AuditLogs], }
      : {},
    "/cohort-size": ["📏 Cohort Size", CohortSize],
    ...permissionLevel >= PermissionLevel.Admin
      ? { "/data-retention": ["💾 Data Retention", DataRetention], }
      : {},
    "/email-reminders": ["📧 Email Reminders", EmailReminders],
    ...permissionLevel >= PermissionLevel.Officer
      ? { "/graduation-pledges": ["🎓 Graduation Pledges", GraduationPledges], }
      : {},
    ...permissionLevel >= PermissionLevel.Officer
      ? { "/home-content": ["🏠 Home Content", HomeContent], }
      : {},
    ...permissionLevel >= PermissionLevel.Officer
      ? { "/manager-approval": ["✅ Manager Approval", ManagerApproval], }
      : {},
    ...permissionLevel >= PermissionLevel.Officer
      ? { "/project-fairs": ["🧪 Project Fairs", ProjectFairs], }
      : {},
    "/manage-users": ["👥 Manage Users", ManageUsers],
    ...permissionLevel >= PermissionLevel.Officer
      ? { "/officer-management": ["♻️ Officer Management", OfficerManagement], }
      : {},
    "/officer-roles": ["🎫 Officer Roles", OfficerRoles],
    "/project-tags": ["🏷️ Project Tags", ProjectTags],
    "/role-tenure": ["⏳ Role Tenure", RoleTenure],
    ...permissionLevel >= PermissionLevel.Admin
      ? { "/site-settings": ["⚙️ Site Settings", SiteSettings], }
      : {},
  };

  // Prism 9.2's prism-menu re-renders forever when `menu` is reassigned with identical
  // content (e.g. a theme toggle re-rendering this page), so only hand it a new array
  // when the visible routes or the active route actually change.
  const routeKey = Object.keys(subRoutes).join(",");
  const menuItems = useMemo(
    () =>
      Object.entries(subRoutes).map(([subRoute, [label, _]], index) => ({
        id: String(index),
        title: label,
        href: `${path}${subRoute}`,
        active: location.pathname === `${path}${subRoute}`,
        root: true,
      })),
    // subRoutes is rebuilt every render; routeKey captures its content.
    [routeKey, location.pathname],
  );

  const handleMenuClick = (e: CustomEvent<any>) => {
    const href = e.detail?.data?.href;
    if (href) {
      navigate(href);
    }
  };

  if (permissionLevel < PermissionLevel.Officer) {
    return <Unauthorized />;
  }

  return (
    <div>
      <PageHeader title="Admin Settings" />

      <div className="admin-container">
        <div className="admin-menu">
          <PrismMenu
            menu={menuItems}
            useCustomRouting
            onPrismClick={handleMenuClick}
            style={{ '--prism-menu-max-width': '100%' } as React.CSSProperties}
          />
        </div>
        <div className="admin-content">
          <Routes>
            {Object.entries(subRoutes).map(([subRoute, [label, AdminPanel]]) => (
              <Route
                key={subRoute}
                path={`${subRoute.slice(1)}/*`}
                element={
                  <div className="admin-panel">
                    <h2>{label}</h2>
                    <AdminPanel params={params} currentUser={currentUser} loginAsUser={loginAsUser} refreshCurrentUser={refreshCurrentUser} />
                  </div>
                }
              />
            ))}
            <Route
              index
              element={
                <div className="admin-panel admin-welcome">
                  <div className="admin-welcome-content">
                    <FontAwesomeIcon icon={faSliders} className="admin-welcome-icon" />
                    <p className="admin-welcome-text">Select an option from the menu to get started</p>
                  </div>
                </div>
              }
            />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;